import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CenterFlex, Flex, VFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import Typo from '@/components/Typography/Typo';
import {
  InsightExplorerMetricType,
  InsightExplorerViewType,
} from '@/features/InsightExplorer/types/constants';
import type {
  InsightExplorerDistributionResponse,
  InsightExplorerTrendResponse,
} from '@/features/InsightExplorer/types/types';
import useColor from '@/hooks/domain/useColor';
import useInsightExplorerStore from '@/stores/InsightExplorerStore';

import CardContainer from '../CardContainer';
import SubtopicsSection from './SubtopicsSection';
import VolumeTrendBySubtopicsContainer from './VolumeTrendBySubtopicsSection/VolumeTrendBySubtopicsContainer';

interface Props {
  data?: InsightExplorerTrendResponse;
  childrenData?: InsightExplorerDistributionResponse;
  isChildrenLoading?: boolean;
}

const SubtopicSectionContainer = (props: Props) => {
  const { data, childrenData, isChildrenLoading } = props;
  const { node_id } = useParams();
  const color = useColor();
  const { selectedNode, view } = useInsightExplorerStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [metricType, setMetricType] = useState(
    InsightExplorerMetricType.VOLUME,
  );

  const sortedChildrenData = useMemo(() => {
    if (view === InsightExplorerViewType.SENTIMENTS) {
      return childrenData?.sort(
        (a, b) => b.negative_volume - a.negative_volume,
      );
    }
    return childrenData;
  }, [view, childrenData]);

  if (!sortedChildrenData) return null;

  return (
    <Transition
      status={
        !isChildrenLoading ||
        (!!node_id &&
          !!selectedNode?.children &&
          selectedNode?.children.length > 0)
      }
      fallback={
        <CardContainer g={16}>
          <CenterFlex style={{ height: 400 }}>
            <Spinner />
          </CenterFlex>
        </CardContainer>
      }
    >
      {sortedChildrenData.length > 0 && (
        <CardContainer g={16}>
          <Flex a="center" style={{ height: 32 }} g={12}>
            <Typo type="label1" weight="sb" color={color.gray800}>
              Sub items
            </Typo>
            <Typo type="label3" weight="m" color={color.gray600}>
              {sortedChildrenData.length} items
            </Typo>
          </Flex>
          <VFlex g={24}>
            <CardContainer
              style={{
                borderColor: color.gray250,
                padding: '20px',
                borderRadius: '8px',
              }}
            >
              <SubtopicsSection
                data={data}
                childrenData={sortedChildrenData}
                currentPage={currentPage}
                totalPages={Math.ceil(sortedChildrenData.length / 5)}
                onPageChange={setCurrentPage}
                metricType={metricType}
                onMetricTypeChange={setMetricType}
              />
            </CardContainer>
            <VolumeTrendBySubtopicsContainer
              childrenData={sortedChildrenData}
              data={data}
            />
          </VFlex>
        </CardContainer>
      )}
    </Transition>
  );
};

export default SubtopicSectionContainer;
